import { useThemeStore } from '@/stores/theme';
import { applyPrimaryTheme } from '@/utils/apply-primary-theme';
import { onMounted, ref, type Ref } from 'vue';

const STORAGE_KEY = '1b-color-mode';

type ColorMode = 'light' | 'dark';

const getInitialMode = (): ColorMode => {
  if (typeof document === 'undefined') {
    return 'light';
  }

  return document.documentElement.getAttribute('data-mode') === 'dark'
    ? 'dark'
    : 'light';
};

const mode: Ref<ColorMode> = ref(getInitialMode());
let initialized = false;

const apply = (next: ColorMode): void => {
  mode.value = next;
  if (next === 'dark') {
    document.documentElement.setAttribute('data-mode', 'dark');
  } else {
    document.documentElement.removeAttribute('data-mode');
  }
};

const persist = (next: ColorMode): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // localStorage may be blocked (private mode); fail silently.
  }
};

const refreshPrimaryForMode = (): void => {
  const store = useThemeStore();
  if (store.basePrimary) {
    store.currentPrimary = applyPrimaryTheme(store.basePrimary);
  }
};

/**
 * Tracks light/dark mode for the 1brain portal.
 *
 * The Blade root sets `data-mode="dark"` BEFORE first paint based on
 * localStorage / `prefers-color-scheme`, so the initial render is already
 * correct. This composable reconciles the Vue-side ref with whatever
 * the boot script decided, then exposes setters that update the DOM,
 * the ref, and localStorage in one place.
 */
export default function useColorMode() {
  onMounted(() => {
    if (initialized) {
      return;
    }
    initialized = true;
    const current =
      document.documentElement.getAttribute('data-mode') === 'dark'
        ? 'dark'
        : 'light';
    mode.value = current;
  });

  const setMode = (next: ColorMode): void => {
    const normalized: ColorMode = next === 'dark' ? 'dark' : 'light';
    apply(normalized);
    persist(normalized);
    refreshPrimaryForMode();
  };

  const toggle = (): void => {
    setMode(mode.value === 'dark' ? 'light' : 'dark');
  };

  return { mode, setMode, toggle };
}
